import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'ip_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true); // true on first load while we verify the token

  // ── Restore session on page load ────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    api.get('/auth/me')
      .then(({ data }) => {
        setUser(data.user);
        setSubscriptions(data.subscriptions || []);
      })
      .catch(() => {
        // The api interceptor already clears the token when it's genuinely
        // expired (401 + local expiry confirmed). For transient network errors
        // we intentionally do NOT remove the token so the session survives
        // a brief server restart or connectivity blip.
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Cross-tab subscription sync ──────────────────────────────────────────────
  // When the user purchases in a new tab (Browse Roles / Study Guides opened
  // from the sidebar), this tab's React state goes stale. Two mechanisms keep
  // it current:
  //  1. storage event — the purchasing tab writes ip_subs_ts; this tab hears it
  //     immediately and re-fetches.
  //  2. visibilitychange — when the user switches back to this tab we re-fetch
  //     as a safety net (covers cases where the storage event fired before the
  //     tab was active).
  useEffect(() => {
    const resync = () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;
      api.get('/auth/me').then(({ data }) => {
        setUser(data.user);
        setSubscriptions(data.subscriptions || []);
      }).catch(() => {});
    };

    const onStorage = (e) => {
      // Another tab purchased/refreshed — sync subscriptions
      if (e.key === 'ip_subs_ts') resync();
      // Another tab's token expired and was cleared — mirror the logout here
      if (e.key === 'ip_token' && !e.newValue) {
        setUser(null);
        setSubscriptions([]);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') resync();
    };

    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // ── Register ────────────────────────────────────────────────────────────────
  const register = useCallback(async (full_name, email, password, phone) => {
    const { data } = await api.post('/auth/register', { full_name, email, password, ...(phone ? { phone } : {}) });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setSubscriptions([]);
    return data;
  }, []);

  // ── Login ───────────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setSubscriptions(data.subscriptions || []);
    return data;
  }, []);

  // ── Google OAuth ────────────────────────────────────────────────────────────
  const loginWithGoogle = useCallback(async (credential) => {
    const { data } = await api.post('/auth/google', { credential });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setSubscriptions(data.subscriptions || []);
    return data;
  }, []);

  // ── Logout ──────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (_) { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setSubscriptions([]);
  }, []);

  // ── Subscription helpers ────────────────────────────────────────────────────
  const hasActiveSubscription = useCallback(
    (roleId) => subscriptions.some((s) =>
      s.status === 'active' && (
        s.job_role_id === roleId ||      // role-specific subscription
        s.tier === 'full_access' ||      // full_access covers all roles
        s.plan_type === 'full_access' || // fallback check
        s.plan_type === 'job_seeker'     // job_seeker covers all roles
      )
    ),
    [subscriptions]
  );

  const refreshSubscriptions = useCallback(async () => {
    if (!user) return;
    const { data } = await api.get('/auth/me');
    setSubscriptions(data.subscriptions || []);
    // Signal other open tabs (dashboard, etc.) to re-fetch subscriptions
    localStorage.setItem('ip_subs_ts', Date.now().toString());
  }, [user]);

  const value = {
    user,
    subscriptions,
    loading,
    isAuthenticated: !!user,
    register,
    login,
    loginWithGoogle,
    logout,
    hasActiveSubscription,
    refreshSubscriptions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
